import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { IPage } from '../../../models/page';
import Title from '../../../components/UI/Title/Title';
import Filters from '../../../components/Container/Filters/Filters';
import { FieldTypes, IFormFields } from '../../../models/form';
import { useAppSelector } from '../../../store';
import { IButton } from '../../../models/button';
import { cellsReportValidation } from '../../../utils/formValidation/cellsReport';
import { ReportsActions } from '../../../store/entities/reports/reports.actions';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { ConfigurationActions } from '../../../store/entities/configuration/configuration.actions';
import { IDynamicList } from '../../../store/entities/general/type';
import { SitesActions } from '../../../store/entities/sites/sites.actions';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import { ISiteList } from '../../../models/site';
import './CellsReports.scss';
import Button from '../../../components/UI/Button/Button';

const CellsReports = ({ permission }: IPage) => {
  const dispatch = useDispatch();
  const { formikProps } = useAppSelector((s) => s.form);
  const { appLanguage, lockerStatuses, siteCompounds, compoundNames } =
    useAppSelector((s) => s.general);
  const { siteListDetails } = useAppSelector((s) => s.sites);

  const [siteList, setSiteList] = useState<IAutoCompleteItem[]>([]);
  const [compoundList, setCompoundList] = useState<IAutoCompleteItem[]>([]);
  const [compoundsLoading, setCompoundsLoading] = useState<boolean>(false);
  const [statusList, setStatusList] = useState<IAutoCompleteItem[]>([]);

  const formikFields = {
    siteIds: 'siteIds',
    compoundIds: 'compoundIds',
    statusIds: 'statusIds',
    cellNumber: 'cellNumber',
    cellId: 'cellId',
    cellNickName: 'cellNickName',
    closetNumber: 'closetNumber',
    lockCaseId: 'lockCaseId',
    cellStatus: 'cellStatus',
    fromPairingDate: 'fromPairingDate',
    toPairingDate: 'toPairingDate',
  };

  // Initial fetches
  useEffect(() => {
    dispatch(GeneralActions.getLockerStatuses());
    dispatch(GeneralActions.getCompoundNames());
    dispatch(ConfigurationActions.getCellsLists());
    dispatch(SitesActions.getSiteListDetails());
  }, [dispatch]);

  // Sites list
  useEffect(() => {
    if (siteListDetails?.length) {
      const active = siteListDetails.filter(
        (s: ISiteList) => s.isActive !== false
      );
      setSiteList(
        active.map((s: ISiteList) => ({
          id: s.id,
          label: appLanguage === 'hebrew' ? s.nameHE : s.nameEN || '',
        }))
      );
    }
  }, [siteListDetails, appLanguage]);

  // Cell Status list
  useEffect(() => {
    if (lockerStatuses?.length) {
      setStatusList(
        lockerStatuses.map((c: IDynamicList) => ({
          id: c.id,
          label: appLanguage === 'hebrew' ? c.nameHE : c.nameEN || '',
        }))
      );
    }
  }, [lockerStatuses, appLanguage]);

  const lastFetchedSitesRef = useRef<string>('');

  // Fetch compounds for selected sites (single or multiple)
  useEffect(() => {
    const selectedSiteIds: number[] = formikProps?.values?.siteIds || [];
    const key = selectedSiteIds.slice().sort().join(',');
    if (key === lastFetchedSitesRef.current) return; // no change
    lastFetchedSitesRef.current = key;

    const fetchCompounds = async () => {
      setCompoundsLoading(true);
      try {
        if (selectedSiteIds.length === 1) {
          // Leverage store for single site (will populate siteCompounds later)
          await dispatch(
            GeneralActions.getCompoundBySite(String(selectedSiteIds[0]))
          );
        } else {
          formikProps?.setFieldValue?.('compoundIds', null);
        }
      } finally {
        setCompoundsLoading(false);
      }
    };
    fetchCompounds();
  }, [formikProps?.values?.siteIds, dispatch, appLanguage]);

  // Build compounds list from store only for single-site selection
  useEffect(() => {
    const selectedSiteIds: number[] = formikProps?.values?.siteIds || [];
    if (selectedSiteIds.length === 1 && siteCompounds?.length) {
      setCompoundList(
        siteCompounds.map((c: any) => {
          const compoundName = compoundNames?.find(
            (name) => name.id === c.compoundNameId
          );
          return {
            id: c.id,
            label:
              appLanguage === 'hebrew'
                ? (compoundName?.nameHE ?? '')
                : (compoundName?.nameEN ?? ''),
          };
        })
      );
    } else {
      setCompoundList([]);
    }
  }, [siteCompounds, appLanguage, formikProps?.values?.siteIds]);

  const searchFields: IFormFields = {
    initialValues: {
      fromPairingDate: null,
      toPairingDate: null,
      siteIds: null,
      compoundIds: null,
      cellId: '',
      cellNumber: '',
      cellNickName: '',
      closetNumber: '',
      lockCaseId: '',
      cellStatus: null,
    },
    formData: [
      {
        type: FieldTypes.DATE,
        id: formikFields.fromPairingDate,
        label: 'lockers.manageLockers.filters.fromPairingDate',
        props: { getStartOfDay: true },
      },
      {
        type: FieldTypes.DATE,
        id: formikFields.toPairingDate,
        label: 'lockers.manageLockers.filters.toPairingDate',
        props: {
          getFullDayTime: true,
          numOfDays: 1,
          minDate: new Date(formikProps?.values?.fromPairingDate),
        },
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: formikFields.siteIds,
        label: 'lockers.manageLockers.filters.siteId',
        items: siteList,
        props: { multiple: true },
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: formikFields.compoundIds,
        label: 'lockers.manageLockers.filters.compoundId',
        items: compoundList,
        props: { multiple: true },
        disabled:
          !formikProps?.values?.siteIds?.length ||
          compoundsLoading ||
          compoundList.length === 0,
      },
      {
        type: FieldTypes.TEXT,
        id: formikFields.cellNumber,
        label: 'lockers.manageLockers.filters.cellNumber',
      },
      {
        type: FieldTypes.TEXT,
        id: formikFields.cellId,
        label: 'lockers.manageLockers.filters.cellId',
      },
      {
        type: FieldTypes.TEXT,
        id: formikFields.cellNickName,
        label: 'lockers.manageLockers.filters.cellNickName',
      },
      {
        type: FieldTypes.TEXT,
        id: formikFields.closetNumber,
        label: 'lockers.manageLockers.filters.closetNumber',
      },
      {
        type: FieldTypes.TEXT,
        id: formikFields.lockCaseId,
        label: 'lockers.manageLockers.filters.lockCaseId',
      },
      {
        type: FieldTypes.AUTOCOMPLETE,
        id: formikFields.cellStatus,
        label: 'lockers.manageLockers.filters.cellStatus',
        items: statusList,
        props: { multiple: false },
      },
    ],
  };

  const exportCellsReport = async (values: any) => {
    if (!permission) return;
    values ??= {};
    dispatch(
      ReportsActions.downloadCellsReport({
        siteIds: values.siteIds,
        compoundIds: values.compoundIds,
        cellNumber: values.cellNumber,
        cellId: values.cellId,
        cellNickName: values.cellNickName,
        closetNumber: values.closetNumber,
        lockCaseId: values.lockCaseId,
        cellStatus: values.cellStatus,
        fromPairingDate: values.fromPairingDate,
        toPairingDate: values.toPairingDate,
      })
    );
  };

  const submitButton: IButton = {
    title: 'button.exportCSV',
    type: 'submit',
    disabled: !cellsReportValidation.isValidSync(formikProps?.values),
  };

  return (
    <div className="cellsReport__wrapper">
      <Title text="reports.cellsReport.pageTitle" />
      <Filters
        fields={searchFields}
        filtersValidation={cellsReportValidation}
        onSubmit={exportCellsReport}
        showSubmitButton={true}
        overridedSubmitButton={<Button {...submitButton} outlined />}
      />
    </div>
  );
};

export default CellsReports;
